import React, { useMemo } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { TextField, Typography, InputAdornment } from "@mui/material";
import { sanitizeDecimal } from "./StorageTrackingUtils";

// ── Darker text for disabled/read-only fields ──────────────────────────────
// MUI's default disabled color is a light gray. WebkitTextFillColor is
// included because Chrome/Safari use it (not `color`) to paint disabled
// and readOnly text inputs, so both need to be forced with !important.
const disabledFieldSx = {
  "& .Mui-disabled": {
    color: "#1a3a5c !important",
    WebkitTextFillColor: "#1a3a5c !important",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#1a3a5c !important",
    WebkitTextFillColor: "#1a3a5c !important",
  },
};

const StorageTrackingTable = ({ rows, onRowChange, isAdmin }) => {
  const handlePercentChange = (rowIndex, value) => {
    // Percent of System Load: digits and up to 16 decimal places.
    onRowChange(rowIndex, "percentOfSystemLoad", sanitizeDecimal(value));
  };

  const tableData = useMemo(
    () =>
      rows.map((row, i) => ({
        rowIndex: i,
        ...row,
      })),
    [rows]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "MarketerName",
        header: "Marketer",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell }) => (
          <Typography
            sx={{
              fontWeight: 600,
              color: "#1a3a5c",
              fontSize: "14px",
              padding: "8px",
            }}
          >
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "percentOfSystemLoad",
        header: "Percent of System Load",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell, row }) => (
          <TextField
            size="small"
            variant="outlined"
            value={cell.getValue() ?? ""}
            onChange={(e) =>
              isAdmin
                ? handlePercentChange(row.original.rowIndex, e.target.value)
                : undefined
            }
            InputProps={{
              readOnly: !isAdmin,
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            disabled={!isAdmin}
            inputProps={{
              style: { textAlign: "center", padding: "5px 8px" },
              inputMode: "decimal",
            }}
            sx={{
              padding: "8px",
              width: "100%",
              minWidth: 90,
              "& .MuiOutlinedInput-root": { borderRadius: "6px" },
              ...disabledFieldSx,
            }}
          />
        ),
      },
      {
        accessorKey: "imbalanceAccountSCQ",
        header: "Imbalance Account SCQ",
        muiTableHeadCellProps: { align: "center" },
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell }) => (
          <Typography
            sx={{
              fontWeight: 600,
              color: "#1a3a5c",
              fontSize: "14px",
              padding: "8px",
            }}
          >
            {cell.getValue()}
          </Typography>
        ),
      },
    ],
    [rows, isAdmin]
  );

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableSorting: false,
    enableColumnFilters: false,
    enableFullScreenToggle: false,
    enableColumnActions: false,
    enableColumnOrdering: false,
    enablePagination: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableTopToolbar: false,
    enableRowActions: false,
    enableRowSelection: false,
    enableHiding: false,
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#1a3a5c",
        color: "#fff",
        fontWeight: 700,
        textAlign: "center",
        borderRight: "1px solid rgba(255,255,255,0.12)",
        py: 1.5,
        "& .Mui-TableHeadCell-Content": {
          justifyContent: "center",
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderRight: "1px solid #e0e7ef",
        py: 0.75,
        px: 1,
        "&:last-child": { borderRight: "none" },
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: "1px solid #e0e7ef",
        borderRadius: "10px",
        overflow: "hidden",
        mt: 2,
      },
    },
    muiTableContainerProps: {
      sx: { maxHeight: 420, overflowX: "auto", overflowY: "auto" },
    },
    enableStickyHeader: true,
  });

  return <MaterialReactTable table={table} />;
};

export default StorageTrackingTable;
